import { AFFILIATE_PARAMS } from '@/utils/tracking/campaign'

import type { LinkedCampaignPropertyInput } from '@fe-toolkit/utility/client/tracking-context'

/**
 * Campaign parameters captured into the CTC session as `linkedCampaigns` (FAAS-787).
 *
 * ## Why this exists when the ggUtmz bridge already captures the same parameters
 *
 * There are two sinks and they serve different consumers, which is easy to conflate:
 *
 * - `server/ggUtmzCookie.ts` writes the `__ggUtmz` cookie, which Portal's
 *   `Web_Plugin_GoogleAnalytics` reads into `App::session('campaign')`. That is what keeps
 *   order-success attribution and Awin conversions alive when a visitor lands here and then
 *   navigates into a Portal journey (FAAS-782). It is tokenised and Portal-shaped.
 * - This list puts the same parameters on the CTC session, which is what BI reports off.
 *
 * Capturing into one does not populate the other. Before this, the affiliate and click-ID
 * parameters reached Portal but were invisible to CTC, so anything reporting from CTC could
 * not attribute a paid or affiliate landing at all.
 *
 * ## The grouping is a guess and MarTech should confirm it
 *
 * `propertyKey` becomes the key on the CTC `linkedCampaigns` entry
 * (`affiliateProperties`, `paidSearchProperties`, ...), and `type` is stored alongside. If BI
 * keys their reports on those strings then the names below are a contract, not a detail. The
 * `affiliate` / `awin` group is copied verbatim from fe-devices, which is in production, so
 * that one is safe. The other two are new and named for what they carry.
 *
 * ## Raw values, not Portal's derived names
 *
 * Portal does not store these raw. It runs a priority ladder and writes a derived campaign
 * name: `gclid` becomes the literal string `paid_search-lastclick` and the gclid itself is
 * discarded; `placementID` and `CreativeID` are concatenated onto the campaign name. We
 * reproduce that faithfully in the `__ggUtmz` payload, because Portal is the consumer there
 * and it expects its own shape (see `resolveCampaignToken`).
 *
 * Here we keep the raw values. A click ID is only useful to downstream analytics if it
 * survives as a click ID, and a report can always derive a name from raw parameters whereas
 * it cannot recover a gclid that was thrown away. This is the difference the ticket asks to
 * be written down rather than guessed at.
 */
export const LINKED_CAMPAIGN_PROPERTIES: LinkedCampaignPropertyInput[] = [
  {
    propertyKey: 'affiliate',
    type: 'awin',
    // `awc` and `pub` are the names Portal's validCampaign() allowlist validates.
    // `publisherId` and `clickRef` are Awin's own naming, which is what fe-devices captures
    // and what the CTC fixtures already carry, and `clickref` is the lowercase spelling that
    // turns up on real Awin links. Listing an absent parameter costs nothing, so all the
    // spellings are here rather than betting on which one a given affiliate link uses.
    contextParams: ['awc', 'pub', 'publisherId', 'clickRef', 'clickref'],
  },
  {
    propertyKey: 'paidSearch',
    type: 'clickId',
    // Portal throws both of these away and synthesises a campaign name instead. Keeping them
    // is the point: a gclid is what ties a session back to a Google Ads click.
    contextParams: ['gclid', 'dclid'],
  },
  {
    propertyKey: 'giffgaffCampaign',
    type: 'giffgaff',
    // Portal appends placementID and CreativeID to the campaign name, so they are only ever
    // readable there by string-splitting. Held separately here.
    contextParams: ['ggCampaign', 'placementID', 'CreativeID', 'sv_campaign_id'],
  },
]

/**
 * Every query parameter the groups above capture, flattened.
 *
 * Exported for the drift guard in the fixtures test: the whole point of building this from
 * the same parameter list the `__ggUtmz` bridge uses is that the two sinks cannot silently
 * diverge, and that only holds if something asserts it.
 */
export const LINKED_CAMPAIGN_PARAMS = LINKED_CAMPAIGN_PROPERTIES.flatMap((group) => group.contextParams)

/**
 * The Portal-validated parameters that must appear in some group above.
 *
 * Re-exported rather than imported directly by the test so the dependency is explicit: this
 * file is what has to change when `AFFILIATE_PARAMS` grows.
 */
export const PORTAL_AFFILIATE_PARAMS = AFFILIATE_PARAMS
